import { memo } from "react";
import { useShallow } from "zustand/react/shallow";

import useRowKeyboardNavigation from "../Table/hooks/useKeyboardNavigation/useRowKeyboardNavigation";
import { selectVisibleColumns } from "../Table/store/useTableStore/selectors";
import { getItemId } from "./helpers";
import useTableSimpleEffectiveColumnPinning from "./hooks/useEffectiveColumnPinning";
import TableSimpleRow from "./Row";
import { TableSimpleDataItem, TableSimpleRowsBaseProps } from "./types";

const TableSimpleRows = <T extends TableSimpleDataItem>({
  data,
  store,
  selectionStore,
  hasMoreSelectionOptions,
  shiftSelectionAction,
  cellPerColumn,
  itemIdKey = "id",
  tableRef,
  stickyRight,
  activeRowId,
}: TableSimpleRowsBaseProps<T>) => {
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });
  const { columns, isFirstColumnPinned } = store(
    useShallow((state) => ({
      columns: selectVisibleColumns(state),
      isFirstColumnPinned: state.isFirstColumnPinned,
    }))
  );

  const { effectiveIsFirstColumnPinned } = useTableSimpleEffectiveColumnPinning(
    tableRef,
    isFirstColumnPinned
  );

  return (
    <>
      {data.map((item) => {
        const itemId = getItemId(item, itemIdKey);

        return (
          <TableSimpleRow
            key={itemId}
            itemId={itemId}
            item={item}
            columns={columns}
            isFirstColumnPinned={effectiveIsFirstColumnPinned}
            selectionStore={selectionStore}
            hasMoreSelectionOptions={hasMoreSelectionOptions}
            shiftSelectionAction={shiftSelectionAction}
            cellPerColumn={cellPerColumn}
            keyboardProps={keyboardProps}
            stickyRight={stickyRight}
            isActive={itemId === activeRowId}
          />
        );
      })}
    </>
  );
};

TableSimpleRows.displayName = "DS.TableSimple.Rows";

export default memo(TableSimpleRows) as typeof TableSimpleRows;
